/**
 * "Why this juror scores this way" — SpatchVenire characteristic assessment,
 * rendered read-only inside the juror-detail modal (Phase 2, reference panel).
 * Shows the matched signals grouped by characteristic, each with its evidence
 * tier and cited sources, plus the confirming voir-dire probes the model wants
 * asked. Explicitly NOT a strike basis — a strike cites a recorded answer.
 *
 * Phase-4 foundation: an evidence-strength toggle drives the model's master gate
 * (Step 1); flipping it re-scores the panel in place. (Full case-side inputs —
 * victim/defendant profiles, panel state, voir-dire answer capture — are the
 * larger remaining Phase-4 build.)
 */

import type { Juror, ChargeSlug, Side, EvidenceStrength } from '@/types';
import { scoreJurorSV } from '@/core/scoring-sv';
import { SV_SOURCES } from '@/data/spatchvenire-model';
import { store, panelContext, svProfile } from '@/app/store';
import { escapeHTML } from '@/utils/formatters';

const BAND_COLOR: Record<string, string> = { GO: '#16a34a', 'NO-GO': '#dc2626', PROBE: '#b45309' };
const TIER_COLOR: Record<string, string> = { RESEARCH: '#1e40af', MECHANISM: '#6366f1', LORE: '#94a3b8' };

function bandPill(label: string, band: string): string {
  const c = BAND_COLOR[band] || '#6b7280';
  return (
    `<span style="display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:700;">` +
    `${label} <span style="background:${c};color:#fff;border-radius:5px;padding:1px 7px;">${band}</span></span>`
  );
}

function evidenceToggle(jurorId: number): string {
  const levels: EvidenceStrength[] = ['weak', 'close', 'strong'];
  const current = store.getState().caseProfile.evidence;
  const btns = levels
    .map((lv) => {
      const on = lv === current;
      return (
        `<button onclick="svSetEvidence(${jurorId},'${lv}')" style="border:1px solid ${on ? '#6366f1' : '#cbd5e1'};` +
        `background:${on ? '#6366f1' : '#fff'};color:${on ? '#fff' : '#475569'};border-radius:6px;padding:2px 9px;` +
        `font-size:10px;font-weight:700;cursor:pointer;text-transform:capitalize;">${lv}</button>`
      );
    })
    .join('');
  return (
    `<div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">` +
    `<span style="font-size:10px;color:var(--text-muted);font-weight:700;">Evidence strength (master gate):</span>${btns}</div>`
  );
}

export function svAssessmentHtml(
  j: Juror,
  ctx: { side: Side; activeCharges: ChargeSlug[] },
  open = false
): string {
  const a = scoreJurorSV(j, ctx, svProfile());

  const byChar = new Map<string, typeof a.matched>();
  for (const m of a.matched) {
    if (!byChar.has(m.characteristic)) byChar.set(m.characteristic, []);
    byChar.get(m.characteristic)!.push(m);
  }

  const favor = a.orientation > 0 ? 'Prosecution-favorable' : a.orientation < 0 ? 'Defense-favorable' : 'Neutral';
  const favorColor = a.orientation > 0 ? '#dc2626' : a.orientation < 0 ? '#16a34a' : '#6b7280';

  const vdNote = a.voirDireAdjustment
    ? `<div style="font-size:10px;color:var(--text-muted);margin-bottom:10px;">Model estimate before answers: ` +
      `${a.modelOrientation > 0 ? '+' : ''}${a.modelOrientation} &nbsp;→&nbsp; voir-dire adjustment: ` +
      `${a.voirDireAdjustment > 0 ? '+' : ''}${a.voirDireAdjustment} &nbsp;→&nbsp; final: ` +
      `${a.orientation > 0 ? '+' : ''}${a.orientation}</div>`
    : '';

  const causeBadge = a.hasCauseFlag
    ? '<span style="background:#b91c1c;color:#fff;border-radius:5px;padding:1px 7px;font-size:10px;font-weight:700;margin-left:8px;">⚑ CAUSE</span>'
    : '';

  let html =
    `<div id="sv-assess-${j.id}">` +
    `<details ${open ? 'open' : ''} style="margin:12px 0;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;">` +
    '<summary style="cursor:pointer;list-style:none;padding:10px 12px;background:#f8fafc;font-size:12px;font-weight:700;color:#334155;display:flex;align-items:center;gap:8px;">' +
    '⚖︎ SpatchVenire assessment ' +
    `<span style="margin-left:auto;color:${favorColor};">${favor} (${a.orientation > 0 ? '+' : ''}${a.orientation})</span>${causeBadge}</summary>` +
    '<div style="padding:10px 12px;">' +
    vdNote +
    evidenceToggle(j.id) +
    '<div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:10px;">' +
    bandPill('Prosecution', a.prosecutionBand) +
    bandPill('Defense', a.defenseBand) +
    '</div>';

  if (byChar.size) {
    for (const [characteristic, sigs] of byChar) {
      html += `<div style="margin-bottom:8px;"><div style="font-size:11px;font-weight:700;color:#334155;">${escapeHTML(characteristic)}</div>`;
      for (const s of sigs) {
        const tc = TIER_COLOR[s.tier] || '#94a3b8';
        const dir = s.contribution > 0 ? '→ prosecution' : s.contribution < 0 ? '→ defense' : '';
        const cites = s.sourceKeys
          .map((k) => SV_SOURCES[k])
          .filter(Boolean)
          .map((c) => `<div style="font-size:9px;color:var(--text-muted);padding-left:10px;">• ${escapeHTML(c)}</div>`)
          .join('');
        html +=
          `<div style="font-size:11px;color:var(--text-secondary);margin:3px 0 3px 8px;">` +
          `<span style="background:${tc};color:#fff;border-radius:4px;padding:0 5px;font-size:9px;font-weight:700;">${s.tier}</span> ` +
          `${escapeHTML(s.condition)} <span style="color:#94a3b8;">${dir}</span>` +
          `</div>${cites}`;
      }
      html += '</div>';
    }
  } else {
    html += '<div style="font-size:11px;color:var(--text-muted);">No characteristic signals matched this juror\'s captured fields.</div>';
  }

  if (a.probes.length) {
    html +=
      '<div style="margin-top:8px;padding-top:8px;border-top:1px dashed #e2e8f0;">' +
      '<div style="font-size:11px;font-weight:700;color:#b45309;">Confirm by voir dire (a strike cites the answer, never this score):</div>' +
      a.probes.map((p) => `<div style="font-size:11px;color:var(--text-secondary);margin-left:8px;">• ${escapeHTML(p)}</div>`).join('') +
      '</div>';
  }

  html +=
    '<div style="font-size:9px;color:var(--text-muted);margin-top:8px;">Reference only — probabilistic signals (mostly MECHANISM/LORE tier pending courtroom calibration). Not a strike basis.</div>' +
    '</div></details></div>';
  return html;
}

/** Re-score at a new evidence strength (Step-1 master gate); persists to the case. */
export function svSetEvidence(jurorId: number, level: EvidenceStrength): void {
  const st = store.getState();
  st.caseProfile = { ...st.caseProfile, evidence: level };
  store.save();
  const j = st.jurors.find((x) => x.id === jurorId);
  if (!j) return;
  const el = document.getElementById(`sv-assess-${jurorId}`);
  if (el) el.outerHTML = svAssessmentHtml(j, panelContext(), true);
}
